import {
  ChangeEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  FocusEvent,
} from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { Tab, Tabs } from "components/Tabs"
import { TargetValues, Universe } from "models/universes"
import {
  createUniverseRecord,
  createUniverseYear,
  deleteUniverseRecord,
  deleteUniverseYear,
  fetchUniverseRecords,
  fetchUniverseYears,
  updateRecordPopulation,
} from "api/universes"
import { css, cx } from "@emotion/css"
import { queryCache, useMutation, useQuery } from "react-query"

import { AddYearModal } from "./AddYearModal"
import AutoCompleteInput from "components/inputs/AutoComplete"
import { COLUMNS } from "./columns"
import { CheckList } from "components/inputs/CheckList"
import { CheckListItemLabel } from "components/inputs/CheckList/CheckListItemLabel"
import { Col } from "components/Rable/types"
import DeleteIcon from "@material-ui/icons/Cancel"
import { Filters } from "components/tables"
import IconButton from "ui/buttons/IconButton"
import { Loading } from "components/Loading"
import { Rable } from "components/Rable"
import { Search } from "routes/Base/Admin/components/Search"
import { WithAuthority } from "components/WithAuthority"
import { fetchMarketTargetParams } from "api/targets"
import isEmpty from "lodash/isEmpty"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useSendFeedBack } from "components/FeedbackSnackbar"
import { useToggle } from "hooks/useToggle"

const getYears = (start: number, stop: number) =>
  Array.from({ length: (stop - start) / -1 + 1 }, (_, i) => start + i * -1)

const years = getYears(2050, 2000)

export const Universes = () => {
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const feedback = useSendFeedBack()
  const actionAllowed = hasAuthority(["AUTH_EDIT_UNIVERSE"])
  const [selectedYear, setSelectedYear] = useState<number | null>()
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const [openCreate, setOpenCreate] = useState<number | null>()
  const { open, onOpen, onClose } = useToggle()
  const isModalOpen = Boolean(modalInfo)
  const [searchValue, setSearchValue] = useState<string>("")
  const [selectedValues, setSelectedValues] =
    useState<Record<string, string[]>>()

  const { data, isLoading } = useQuery(["universe-years"], fetchUniverseYears)

  // ** fetch target-parameter
  const { data: targetParams } = useQuery(
    ["target-param"],
    fetchMarketTargetParams,
  )

  const filters = useMemo(() => {
    const genderFilterOptions =
      targetParams
        ?.find((t) => t.semantic === "GENDER")
        ?.variables?.map((p) => ({
          label: <CheckListItemLabel secondary={""} primary={p} />,
          searchLabel: p,
          value: p,
        })) || []

    const ageRangeFilterOptions =
      targetParams
        ?.find((t) => t.semantic === "AGE_RANGE")
        ?.variables?.map((p) => ({
          label: <CheckListItemLabel secondary={""} primary={p} />,
          searchLabel: p,
          value: p,
        })) || []
    const socioeconomicsFilterOptions =
      targetParams
        ?.find((t) => t.semantic === "SOCIOECONOMICS")
        ?.variables?.map((p) => ({
          label: <CheckListItemLabel secondary={""} primary={p} />,
          searchLabel: p,
          value: p,
        })) || []
    const typeOfWorkFilterOptions =
      targetParams
        ?.find((t) => t.semantic === "TYPE_OF_WORK")
        ?.variables?.map((p) => ({
          label: <CheckListItemLabel secondary={""} primary={p} />,
          searchLabel: p,
          value: p,
        })) || []

    const options = [
      {
        name: "AGE_RANGE",
        label: "filters.ageRange",
        options: ageRangeFilterOptions,
      },
      {
        name: "SOCIOECONOMICS",
        label: "filters.socioeconomics",
        options: socioeconomicsFilterOptions,
      },
      {
        name: "GENDER",
        label: "filters.gender",
        options: genderFilterOptions,
      },
      {
        name: "TYPE_OF_WORK",
        label: "filters.typeOfWork",
        options: typeOfWorkFilterOptions,
      },
    ]

    return options
  }, [targetParams])

  const genderOptions =
    targetParams
      ?.find((t) => t.semantic === "GENDER")
      ?.variables?.map((p) => ({
        label: p,
        value: p,
      })) || []

  const ageRangeOptions =
    targetParams
      ?.find((t) => t.semantic === "AGE_RANGE")
      ?.variables?.map((p) => ({
        label: p,
        value: p,
      })) || []
  const socioeconomicsOptions =
    targetParams
      ?.find((t) => t.semantic === "SOCIOECONOMICS")
      ?.variables?.map((p) => ({
        label: p,
        value: p,
      })) || []
  const typeOfWorkOptions =
    targetParams
      ?.find((t) => t.semantic === "TYPE_OF_WORK")
      ?.variables?.map((p) => ({
        label: p,
        value: p,
      })) || []

  const [create] = useMutation(createUniverseYear, {
    onSuccess: () => {
      queryCache.invalidateQueries("universe-years")
      onClose()
    },
    onError: (err) => {
      feedback(t(err.message), "error")
    },
  })

  const [tab, setTab] = useState<number | undefined>()

  const universeId = !!data?.length
    ? data?.find((d) => d?.year === tab)?.id || ""
    : ""

  useEffect(() => {
    setOpenCreate(undefined)
    !!data?.length && setTab(data[0]?.year)
  }, [data])

  const { data: records, isLoading: recordsLoading } = useQuery(
    ["universe-records", universeId],
    fetchUniverseRecords,
  )

  const [createRecord] = useMutation(createUniverseRecord, {
    onSuccess: () => {
      setOpenCreate(undefined)
      queryCache.invalidateQueries(["universe-records", universeId])
    },
    onError: (err) => {
      feedback(t(err.message), "error")
    },
  })
  const onCreate = (newRow: Partial<Universe>) => {
    const { population, ...rest } = newRow
    const _newRow = Object?.keys(rest).reduce((acc, curr) => {
      const _key = curr?.split(".")
      return { ...acc, [_key[1]]: newRow[curr as keyof typeof newRow] }
    }, {}) as TargetValues
    return createRecord({ population, variables: { ..._newRow }, universeId })
  }

  const [patch] = useMutation(updateRecordPopulation, {
    onSuccess: () => {
      queryCache.invalidateQueries(["universe-records", universeId])
    },
    onError: (err) => {
      feedback(t(err.message), "error")
    },
  })

  const onUpdate = useCallback(
    (rowId: string, row: Partial<Universe>) => {
      patch({
        recordId: rowId,
        population: row?.population || 0,
      })
    },
    [patch],
  )

  const [remove] = useMutation(deleteUniverseRecord, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries(["universe-records", universeId])
    },
    onError: (err) => {
      setModalInfo(undefined)
      feedback(t(err.message), "error")
    },
  })

  const onDelete = useCallback(
    (rowId: string) => {
      setModalInfo({
        title: t("market.universe.modal.delete.title"),
        type: "primary_destructive",
        description: t("market.universe.modal.delete.description"),
        submitLabel: t("modal.label.delete"),
        onSubmit: async () => await remove({ universeId: rowId || "" }),
      })
    },
    [remove, t],
  )

  const [removeYear] = useMutation(deleteUniverseYear, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("universe-years")
    },
    onError: (err) => {
      setModalInfo(undefined)
      feedback(t(err.message), "error")
    },
  })

  const handleTabDelete = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("market.universe.delete.title"),
        type: "primary_destructive",
        description: t("market.universe.delete.description"),
        submitLabel: t("actions.delete"),
        onSubmit: async () => await removeYear(id),
      })
    },
    [t, removeYear],
  )

  const onYearCreate = (value?: string | number | null) => {
    const v = typeof value === "number" ? value : Number(value)
    create({ year: v })
  }

  const handleBlur = (
    evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = evt?.target
    !!value && onYearCreate(value)
  }

  const handleChange = (value: number | null) => setSelectedYear(value)

  const onTabChange = (value: number) => {
    setTab(value)
    setOpenCreate(undefined)
  }

  const handleSearchChange = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = evt.target
    setSearchValue(() => value)
  }

  const handleSearchClear = () => {
    setSearchValue(() => "")
  }

  const onOpenCreate = () => setOpenCreate(tab)
  const onCloseCreate = () => setOpenCreate(undefined)

  const required = COLUMNS?.filter((c) => c.required) as Col<
    Partial<Universe>
  >[]
  const newRowCols = COLUMNS?.filter((c) => c.existsInCreate) as Col<
    Partial<Universe>
  >[]

  const newUniverseDisable = () => (row: Partial<Universe>) => {
    const { population, ...rest } = row
    const variables = Object?.keys(rest).reduce((acc, curr) => {
      const _key = curr?.split(".")
      return { ...acc, [_key[1]]: row[curr as keyof typeof row] }
    }, {}) as TargetValues
    return population === undefined || isEmpty(variables)
  }

  const handleFiltersReset = () => {
    setSelectedValues(undefined)
  }
  const activeFiltersCount = Object.values(selectedValues || {})
    .map((k) => k)
    .flat().length

  const context = {
    genderOptions,
    ageRangeOptions,
    socioeconomicsOptions,
    typeOfWorkOptions,
  }

  const filteredRecords = records?.filter((record) => {
    let searchMatch = !searchValue

    if (searchValue) {
      searchMatch = Object.values(record.variables).some((v) =>
        v?.includes(searchValue),
      )
    }
    if (!searchMatch) return false

    if (!selectedValues) return record

    return Object.keys(selectedValues)?.every(
      (key) =>
        !selectedValues[key]?.length ||
        selectedValues[key].includes(
          record.variables?.[key as keyof TargetValues],
        ),
    )
  })

  return (
    <UniverseContextOptions.Provider value={context}>
      {isLoading ? (
        <Loading />
      ) : !!data?.length ? (
        <>
          <Tabs
            value={tab}
            TabIndicatorProps={{
              style: {
                display: "none",
              },
            }}
            scrollButtons="auto"
            onTabChange={onTabChange}
          >
            {data?.map((tb, idx) => (
              <Tab
                key={idx}
                className={styles.tabClass}
                value={tb?.year}
                label={
                  <span className={styles.tabLabel}>
                    <span className={styles.label}>{tb?.year}</span>
                    {hasAuthority(["AUTH_DELETE_UNIVERSE"]) ? (
                      <DeleteIcon
                        onClick={() => handleTabDelete(tb?.id)}
                        className={styles.icon}
                      />
                    ) : null}
                  </span>
                }
              />
            ))}
            {hasAuthority(["AUTH_CREATE_UNIVERSE"]) ? (
              <div className={styles.addButtonContainer}>
                <IconButton onClick={onOpen} className={styles.addButton}>
                  +
                </IconButton>
              </div>
            ) : null}
          </Tabs>
          <div className={styles.tableContainer}>
            <div className={styles.search}>
              <Search
                name={"name"}
                value={searchValue}
                handleChange={handleSearchChange}
                handleClear={handleSearchClear}
                placeholder={t("inputs.search.placeholder")}
              />
              <div className={styles.filters}>
                <Filters
                  title={t("market.universes.filters.title")}
                  count={activeFiltersCount}
                  onReset={handleFiltersReset}
                  containerHeight="350"
                >
                  {filters.map((filter) => {
                    const options = filter.options.map((option) => {
                      const checked = selectedValues?.[filter.name]?.includes(
                        option.value,
                      )

                      return {
                        ...option,
                        checked,
                        setValue: (name: string, value: string) => {
                          const exists = selectedValues?.[name]?.includes(value)

                          if (!exists) {
                            setSelectedValues((selected) => ({
                              ...(selected || {}),
                              [name]: [...(selected?.[name] || []), value],
                            }))
                          } else {
                            setSelectedValues((selected) => ({
                              ...(selected || {}),
                              [name]: [...(selected?.[name] || [])].filter(
                                (v) => v !== value,
                              ),
                            }))
                          }
                        },
                      }
                    })

                    const label = t(filter.label)
                    return (
                      <CheckList
                        key={filter.name}
                        name={filter.name}
                        label={label}
                        options={options}
                        totalChecked={activeFiltersCount}
                      />
                    )
                  })}
                </Filters>
              </div>
            </div>
            <div
              className={cx({
                [styles.emptyMargin]: !records?.length,
                [styles.withMargin]: !hasAuthority(["AUTH_CREATE_UNIVERSE"]),
              })}
            >
              <Rable<Universe>
                data={filteredRecords || []}
                columns={COLUMNS}
                required={required}
                createRow={onCreate}
                newRowColumns={newRowCols}
                updateRow={onUpdate}
                label="rate.card.loading"
                open={openCreate === tab}
                onClose={onCloseCreate}
                actionWidth={80}
                onOpen={
                  hasAuthority(["AUTH_CREATE_UNIVERSE"])
                    ? onOpenCreate
                    : undefined
                }
                pending={recordsLoading}
                hasActions={actionAllowed}
                actions={(row: Universe) => [
                  {
                    label: t("actions.delete"),
                    hidden: false,
                    onClick: () => onDelete(row?.id),
                    labelStyle: styles.deleteLabel,
                  },
                ]}
                createButtonLabel={t("market.universe.add.record")}
                hasToolbar={!!records?.length}
                disabledCondition={newUniverseDisable()}
                emptyState={
                  !records?.length && !openCreate ? (
                    <>
                      <div className={styles.emptyMessage}>
                        {t("market.universes.empty.table")}
                      </div>
                      <WithAuthority allowed={["AUTH_CREATE_UNIVERSE"]}>
                        <div
                          onClick={() => setOpenCreate(tab)}
                          className={styles.emptyUniverse}
                        >
                          {t("market.universes.empty.add.universe")}
                        </div>
                      </WithAuthority>
                    </>
                  ) : !filteredRecords?.length ? (
                    <div className={styles.emptyMessage}>
                      {t("market.universes.empty.table.filtered")}
                    </div>
                  ) : null
                }
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={cx("BodyBody-1BlackBold", styles.title)}>
            {t("market.universe.empty.add.title")}
          </div>
          <div className="BodyBody-1BlackLight">
            {t("market.universe.empty.add.subtitle")}
          </div>
          <AutoCompleteInput<number | null>
            label={t("market.universe.input.type")}
            size="medium"
            margin="normal"
            options={years || []}
            onChange={handleChange}
            onBlur={handleBlur}
            getOptionSelected={(option, value) => option === value}
            renderOption={(option) => <span>{option}</span>}
            getOptionLabel={(option) => `${option}`}
            value={selectedYear}
            fetchOnFilter={false}
            className={styles.input}
            type="number"
            fullWidth={false}
          />
        </>
      )}
      <AddYearModal
        years={years || []}
        open={open}
        onClose={onClose}
        onCreate={onYearCreate}
      />
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
    </UniverseContextOptions.Provider>
  )
}

export const UniverseContextOptions = createContext<
  | {
      genderOptions: {
        label: string
        value: string
      }[]
      ageRangeOptions: {
        label: string
        value: string
      }[]
      socioeconomicsOptions: {
        label: string
        value: string
      }[]
      typeOfWorkOptions: {
        label: string
        value: string
      }[]
    }
  | undefined
>(undefined)

export const useContextOptions = () => {
  const options = useContext(UniverseContextOptions)

  if (options === undefined) {
    throw new Error("error")
  }
  return options
}

const styles = {
  title: css`
    padding: var(--spacing-xs) 0;
  `,
  input: css`
    min-width: 255px;
    margin-top: var(--spacing-xxl-2-1);
  `,
  addButtonContainer: css`
    display: flex;
    align-items: center;
    padding-left: 14px;
  `,
  addButton: css`
    width: var(--spacing-m);
    height: var(--spacing-m);
    font-size: 16px;
    min-width: var(--spacing-m);
    background-color: var(--primary-main);
    color: var(--white);
    padding-top: 2px;
    --btn-bg-hover: var(--primary-main-extra-dark);
    --btn-border-color-hover: var(--primary-main-extra-dark);
    :focus {
      box-shadow: none;
      color: var(--white);
      background-color: var(--primary-main-extra-dark);
    }
  `,
  tabClass: css`
    min-height: var(--spacing-xxl-2);
    &.MuiButtonBase-root {
      background-color: var(--lighter-grey);
      padding: var(--spacing-xs) var(--spacing-m);
      min-width: 0;
      border: solid 1px var(--light-grey);
      border-top-right-radius: 4px;
      border-top-left-radius: 4px;
      border-bottom: solid 1px var(--light-grey);
      span {
        font-size: 13px;
        color: var(--dark-grey);
        text-transform: none;
      }
    }
    &.MuiButtonBase-root.Mui-selected {
      background-color: var(--white);
      border-bottom: solid 2px var(--white);
      span {
        color: var(--black);
        font-weight: normal;
      }
    }
  `,
  tableContainer: css`
    position: relative;
    padding: var(--spacing-xxl-2) var(--spacing-m) var(--spacing-m);
    border: solid 1px var(--light-grey);
    border-top: none;
  `,
  tabLabel: css`
    display: flex;
    align-items: center;
    justify-content: space-around;
    width: 100%;
  `,
  label: css`
    min-width: 50px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  `,
  icon: css`
    object-fit: contain;
    color: var(--disabled-grey);
    width: 14px;
    margin: 0 4px;
  `,
  emptyMessage: css`
    margin: 0;
    margin-bottom: 8px;
    padding-top: var(--spacing-xxl-2);
    font-size: 16px;
    font-weight: 300;
    text-align: center;
    color: var(--dark-grey);
  `,
  emptyUniverse: css`
    font-size: 13px;
    font-weight: bold;
    text-align: center;
    color: var(--primary-main);
    cursor: pointer;
  `,
  createUniverse: css`
    align-items: center;
    border: dashed 1px var(--primary-main);
    height: 40px;
    padding: 11px 39px 11px 14px;
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    color: var(--dark-grey);
    display: none;
  `,
  createUniverseOpen: css`
    display: flex;
  `,
  inputClass: css`
    outline: none;
    border: none;
  `,
  cancelButton: css`
    margin-left: var(--spacing-xs);
    padding: 5px 10px 5px 10px;
    flex-direction: column;
  `,
  createButton: css`
    margin-left: var(--spacing-xs);
    padding: 5px 10px 5px 10px;
  `,
  search: css`
    display: flex;
    position: absolute;
  `,
  emptyMargin: css`
    margin-top: var(--spacing-xxl-2);
  `,
  withMargin: css`
    margin-top: var(--spacing-xxl);
  `,
  filters: css`
    padding-left: var(--spacing-m);
  `,
  deleteLabel: css`
    > span {
      color: var(--red);
    }
  `,
}
